import { useState } from "react";
import { Upload, FileText, CheckCircle2, AlertTriangle, Loader2, ArrowLeft, HelpCircle } from "lucide-react";
import { HalfPriceRulesModal } from "@/components/half-price/HalfPriceRulesModal";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { toast } from "@/hooks/use-toast";
import {
  uploadHalfPriceDocument,
  isAcceptedHalfPriceFile,
  HALF_PRICE_ACCEPT_ATTR,
  HALF_PRICE_MAX_BYTES,
} from "@/lib/half-price";
import { useTranslation } from "react-i18next";

export interface HalfPriceDocTicket {
  id: string;
  eventId: string;
  tierName: string;
  attendeeName?: string | null;
}

type UploadStatus = "idle" | "uploading" | "done" | "error";

interface CheckoutStepHalfPriceDocsProps {
  tickets: HalfPriceDocTicket[];
  onComplete: () => void;
  onBack: () => void;
  isFinalizing?: boolean;
}

export function CheckoutStepHalfPriceDocs({
  tickets,
  onComplete,
  onBack,
  isFinalizing = false,
}: CheckoutStepHalfPriceDocsProps) {
  const { t } = useTranslation("checkout");
  const [status, setStatus] = useState<Record<string, UploadStatus>>({});
  const [fileNames, setFileNames] = useState<Record<string, string>>({});
  const [rulesOpen, setRulesOpen] = useState(false);

  const allDone = tickets.length > 0 && tickets.every((t) => status[t.id] === "done");

  const handleFile = async (ticket: HalfPriceDocTicket, file: File | undefined) => {
    if (!file) return;

    if (!isAcceptedHalfPriceFile(file)) {
      toast({
        title: t("checkout.halfPriceDocs.invalidFormatTitle"),
        description: t("checkout.halfPriceDocs.invalidFormatDesc"),
        variant: "destructive",
      });
      return;
    }
    if (file.size > HALF_PRICE_MAX_BYTES) {
      toast({
        title: t("checkout.halfPriceDocs.fileTooLargeTitle"),
        description: t("checkout.halfPriceDocs.fileTooLargeDesc"),
        variant: "destructive",
      });
      return;
    }

    setFileNames((p) => ({ ...p, [ticket.id]: file.name }));
    setStatus((p) => ({ ...p, [ticket.id]: "uploading" }));
    try {
      await uploadHalfPriceDocument({ ticketId: ticket.id, eventId: ticket.eventId, file });
      setStatus((p) => ({ ...p, [ticket.id]: "done" }));
    } catch (err) {
      console.error("[half-price] upload error:", err);
      setStatus((p) => ({ ...p, [ticket.id]: "error" }));
      toast({
        title: t("checkout.halfPriceDocs.uploadFailedTitle"),
        description: err instanceof Error ? err.message : t("checkout.halfPriceDocs.uploadFailedDesc"),
        variant: "destructive",
      });
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-display font-bold text-foreground">{t("checkout.halfPriceDocs.title")}</h2>
        <p className="text-sm text-muted-foreground mt-1">
          {t("checkout.halfPriceDocs.subtitle")}
        </p>
      </div>

      <div className="flex items-start gap-3 p-3.5 rounded-xl border border-primary/35 bg-primary/5 text-sm">
        <AlertTriangle className="h-5 w-5 text-primary shrink-0 mt-0.5" />
        <div className="min-w-0">
          <p className="text-primary dark:text-primary/95 font-medium">{t("checkout.halfPriceDocs.disclaimer")}</p>
          <button
            type="button"
            onClick={() => setRulesOpen(true)}
            className="mt-1.5 inline-flex items-center gap-1 text-xs font-medium text-primary underline underline-offset-2 transition-transform duration-150 ease-out active:scale-[0.97]"
          >
            <HelpCircle className="h-3.5 w-3.5" />
            {t("checkout.halfPriceDocs.howItWorks")}
          </button>
        </div>
      </div>

      <HalfPriceRulesModal open={rulesOpen} onOpenChange={setRulesOpen} audience="buyer" />

      <div className="space-y-3">
        {tickets.map((ticket, idx) => {
          const st = status[ticket.id] || "idle";
          return (
            <Card key={ticket.id} className="border-border/60">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-semibold flex items-center justify-between">
                  <span>
                    {ticket.tierName}
                    {tickets.length > 1 && <span className="text-muted-foreground"> · {t("checkout.halfPriceDocs.ticketNum", { number: idx + 1 })}</span>}
                  </span>
                  {st === "done" && (
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600">
                      <CheckCircle2 className="h-4 w-4" /> {t("checkout.halfPriceDocs.sent")}
                    </span>
                  )}
                </CardTitle>
                {ticket.attendeeName && (
                  <CardDescription className="text-xs">{ticket.attendeeName}</CardDescription>
                )}
              </CardHeader>
              <CardContent>
                <label
                  className={`flex items-center gap-3 p-3 rounded-lg border border-dashed cursor-pointer transition-colors ${
                    st === "done"
                      ? "border-emerald-500/40 bg-emerald-500/5"
                      : st === "error"
                      ? "border-destructive/40 bg-destructive/5"
                      : "border-border hover:border-primary/50 hover:bg-muted/30"
                  }`}
                >
                  <input
                    type="file"
                    accept={HALF_PRICE_ACCEPT_ATTR}
                    className="hidden"
                    disabled={st === "uploading" || isFinalizing}
                    onChange={(e) => handleFile(ticket, e.target.files?.[0])}
                  />
                  {st === "uploading" ? (
                    <Loader2 className="h-5 w-5 text-primary animate-spin shrink-0" />
                  ) : st === "done" ? (
                    <FileText className="h-5 w-5 text-emerald-600 shrink-0" />
                  ) : (
                    <Upload className="h-5 w-5 text-muted-foreground shrink-0" />
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-foreground truncate">
                      {fileNames[ticket.id] || t("checkout.halfPriceDocs.selectDocument")}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {st === "uploading"
                        ? t("checkout.halfPriceDocs.uploading")
                        : st === "done"
                        ? t("checkout.halfPriceDocs.tapToReplace")
                        : st === "error"
                        ? t("checkout.halfPriceDocs.uploadFailedTryAgain")
                        : t("checkout.halfPriceDocs.formatAndSizeNotice")}
                    </p>
                  </div>
                </label>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="flex items-center justify-between gap-3 pt-2">
        <Button variant="outline" onClick={onBack} disabled={isFinalizing}>
          <ArrowLeft className="h-4 w-4 mr-1.5" /> {t("checkout.halfPriceDocs.back")}
        </Button>
        <Button onClick={onComplete} disabled={!allDone || isFinalizing}>
          {isFinalizing ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : null}
          {t("checkout.halfPriceDocs.continue")}
        </Button>
      </div>
    </div>
  );
}
